import classNames from 'classnames'
import React, { FC, ReactNode, CSSProperties } from 'react'

import { generateSrcSet } from 'project/image-proxy-web'
import useWindowSize from 'components/util/useWindowSize'

// ============================================================================
// Types
// ============================================================================

type ImageSource = {
  src: string
  dimensions: {
    width: number
    height: number
  }
}

type MediaImage = {
  alt?: string
  desktop: ImageSource
  mobile: ImageSource
}

type LayoutPreset =
  | 'content-left-media-right'
  | 'content-right-media-left'
  | 'media-top-content-bottom'
  | 'content-top-media-bottom'

type ImageFitPreset =
  | 'cover-left'
  | 'cover-right'
  | 'cover-center'
  | 'cover-top'
  | 'contain-center'
  | 'contain-top'
  | 'custom'

type ResponsiveWidth =
  | string
  | {
    desktop?: string
    mobile?: string
  }

type MediaCardProps = {
  className?: string
  image: MediaImage
  children: ReactNode
  headerContent?: ReactNode
  footerContent?: ReactNode
  layout?: {
    desktop?: LayoutPreset
    mobile?: LayoutPreset
  }
  imageFitPreset?: ImageFitPreset
  customImageStyle?: CSSProperties
  imageContainerWidth?: ResponsiveWidth
  imageClassName?: string
  imageContainerClassName?: string
  contentClassName?: string
  headerClassName?: string
  footerClassName?: string
}

// ============================================================================
// Helper Functions
// ============================================================================

const getImageFitStyle = (preset: ImageFitPreset): CSSProperties => {
  const styles: Record<ImageFitPreset, CSSProperties> = {
    'cover-left': {
      objectFit: 'cover',
      objectPosition: 'left',
      height: '100%',
      maxWidth: '100%',
    },
    'cover-right': {
      objectFit: 'cover',
      objectPosition: 'right',
      height: '100%',
      maxWidth: '100%',
    },
    'cover-center': {
      objectFit: 'cover',
      objectPosition: 'center',
      height: '100%',
      maxWidth: '100%',
    },
    'cover-top': {
      objectFit: 'cover',
      objectPosition: 'top',
      height: '100%',
      maxWidth: '100%',
    },
    'contain-center': {
      objectFit: 'contain',
      objectPosition: 'center',
      height: '100%',
      maxWidth: '100%',
    },
    'contain-top': {
      objectFit: 'contain',
      objectPosition: 'top',
      height: '100%',
      maxWidth: '100%',
    },
    custom: {},
  }
  return styles[preset]
}

const getResponsiveWidth = (
  width: ResponsiveWidth | undefined,
  isMobile: boolean,
): string => {
  if (!width) {
    return isMobile ? '100%' : '50%'
  }

  if (typeof width === 'string') {
    return isMobile ? '100%' : width
  }

  return isMobile ? width.mobile ?? '100%' : width.desktop ?? '50%'
}

const getFlexDirection = (
  isMobile: boolean,
  desktopLayout: LayoutPreset,
  mobileLayout: LayoutPreset,
): string => {
  const currentLayout = isMobile ? mobileLayout : desktopLayout

  switch (currentLayout) {
    case 'content-left-media-right':
      return 'flex-row-reverse'
    case 'content-right-media-left':
      return 'flex-row'
    case 'media-top-content-bottom':
      return 'flex-column'
    case 'content-top-media-bottom':
      return 'flex-column-reverse'
    default:
      return 'flex-row'
  }
}

// ============================================================================
// Component
// ============================================================================

const MediaCard: FC<MediaCardProps> = ({
  children,
  className,
  image,
  headerContent,
  footerContent,
  layout,
  imageFitPreset = 'cover-left',
  customImageStyle,
  imageContainerWidth,
  imageClassName,
  imageContainerClassName,
  contentClassName,
  headerClassName,
  footerClassName,
}) => {
  const size = useWindowSize()
  const isMobile = !size.isLgOrHigher

  const desktopLayout = layout?.desktop ?? 'content-left-media-right'
  const mobileLayout = layout?.mobile ?? 'media-top-content-bottom'

  const imageStyle = customImageStyle ?? getImageFitStyle(imageFitPreset)
  const containerWidth = getResponsiveWidth(imageContainerWidth, isMobile)
  const imageAlt = image.alt ?? 'Decorative image'
  const flexDirection = getFlexDirection(isMobile, desktopLayout, mobileLayout)

  const useAbsolutePositioning =
    !isMobile &&
    (desktopLayout === 'content-left-media-right' ||
      desktopLayout === 'content-right-media-left') &&
    size.isXlOrHigher &&
    imageFitPreset !== 'contain-center' &&
    imageFitPreset !== 'contain-top'

  const imageSource = isMobile ? image.mobile : image.desktop

  const isCoverPreset = imageFitPreset.startsWith('cover')
  const imageDimensionConstraints = isCoverPreset
    ? {}
    : { maxHeight: `${imageSource.dimensions.height}px` }

  return (
    <div className={classNames('card media-card overflow-hidden', className)}>
      {headerContent && (
        <div className={classNames('media-card__header', headerClassName)}>
          {headerContent}
        </div>
      )}

      <div className={classNames('d-flex', flexDirection)}>
        <div
          className={classNames(
            'media-card__media-container',
            'd-flex flex-column justify-content-center position-relative flex-shrink-0',
            imageContainerClassName,
          )}
          style={{ width: containerWidth }}
        >
          {imageSource.src !== 'none' && (
            <img
              {...generateSrcSet(
                imageSource.src,
                imageSource.dimensions.width,
                imageSource.dimensions.height,
              )}
              loading="lazy"
              alt={imageAlt}
              className={classNames('media-card__image', imageClassName)}
              style={
                useAbsolutePositioning
                  ? {
                    position: 'absolute',
                    bottom: 0,
                    right:
                      desktopLayout === 'content-left-media-right'
                        ? 0
                        : undefined,
                    left:
                      desktopLayout === 'content-right-media-left'
                        ? 0
                        : undefined,
                    ...imageStyle,
                    ...imageDimensionConstraints,
                  }
                  : {
                    ...imageStyle,
                    ...imageDimensionConstraints,
                  }
              }
            />
          )}
        </div>

        <div className={classNames('media-card__content', contentClassName)}>
          {children}
        </div>
      </div>

      {footerContent && (
        <div className={classNames('media-card__footer', footerClassName)}>
          {footerContent}
        </div>
      )}
    </div>
  )
}

export default MediaCard
